import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, MapPin, SpinnerGap, Warning } from "@phosphor-icons/react";
import SearchBar from "../components/SearchBar";
import SpecialtyCard from "../components/SpecialtyCard";
import ProFeaturesShowcase from "../components/ProFeaturesShowcase";
import { Button } from "../components/ui/button";
import { RecentlyVerifiedWidget } from "./RecentlyVerified";
import { ALL_STATES } from "../lib/seoContent";
import { webSiteSchema, breadcrumbSchema } from "../lib/jsonLd";

const SPECIALTIES = [
  { name: "Cardiology", slug: "cardiology", icon: "heart" },
  { name: "Dermatology", slug: "dermatology", icon: "skin" },
  { name: "Pediatrics", slug: "pediatrics", icon: "baby" },
  { name: "Psychiatry", slug: "psychiatry", icon: "brain" },
  { name: "Orthopedics", slug: "orthopedics", icon: "bone" },
  { name: "Family Medicine", slug: "family-medicine", icon: "family" },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);

  const handleSpecialtyClick = (specialty) => {
    navigate(`/search?taxonomy_description=${encodeURIComponent(specialty.name)}`);
  };

  const handleFindNearMe = async () => {
    setLocating(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setLocating(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Use OpenStreetMap Nominatim for reverse geocoding (free, no API key)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'User-Agent': 'NPI-Lookup-App'
              }
            }
          );
          
          if (!response.ok) {
            throw new Error('Failed to get location');
          }
          
          const data = await response.json();
          const postalCode = data.address?.postcode;
          
          if (postalCode) {
            // Navigate to clean URL with postal code
            const cleanPostal = postalCode.replace(/[^0-9]/g, '').substring(0, 5);
            navigate(`/near-me/${cleanPostal}/10?lat=${latitude}&lng=${longitude}`);
          } else {
            // Fallback: use state if no postal code
            const state = data.address?.state;
            if (state) {
              // Map state name to code
              const stateCode = getStateCode(state);
              if (stateCode) {
                navigate(`/state/${stateCode}`);
              } else {
                setLocationError("Could not determine your location. Please search manually.");
              }
            } else {
              setLocationError("Could not determine your location. Please search manually.");
            }
          }
        } catch (error) {
          console.error("Geocoding error:", error);
          setLocationError("Failed to determine your location. Please search manually.");
        } finally {
          setLocating(false);
        }
      },
      (error) => {
        setLocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError("Location access denied. Please enable location permissions.");
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError("Location information unavailable.");
            break;
          case error.TIMEOUT:
            setLocationError("Location request timed out.");
            break;
          default:
            setLocationError("An error occurred while getting your location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  };

  // Helper to convert state name to code
  const getStateCode = (stateName) => {
    const states = {
      "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
      "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
      "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
      "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
      "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS", "Missouri": "MO",
      "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
      "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH",
      "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
      "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT",
      "Virginia": "VA", "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY",
      "District of Columbia": "DC"
    };
    return states[stateName] || null;
  };

  return (
    <>
      <Helmet>
        <title>NPI Lookup — Free NPI Number Search & Registry Lookup</title>
        <meta 
          name="description" 
          content="Free NPI Lookup — search 6.2M+ US healthcare providers by NPI number, name, or specialty in the NPPES national registry. Verify PECOS & OIG status instantly." 
        />
        <script type="application/ld+json">{JSON.stringify(webSiteSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([{ name: "Home" }]))}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-bg relative">
        <div className="hero-overlay">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            {/* Provider Counter Badge */}
            <div className="flex justify-center mb-6">
              <div 
                className="inline-flex items-center gap-2 bg-teal/10 text-teal px-4 py-2 rounded-full"
                data-testid="provider-counter"
              >
                <CheckCircle className="w-5 h-5" weight="fill" />
                <span className="font-medium text-sm">6.2M+ providers registered</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-navy text-center mb-4 tracking-tight">
              NPI Lookup
            </h1>
            
            {/* Tagline */}
            <p className="text-lg sm:text-xl text-slate-600 text-center mb-10 max-w-2xl mx-auto">
              The fastest way to verify any US healthcare provider
            </p>

            {/* Search Bar */}
            <div className="mb-6" data-testid="homepage-search">
              <SearchBar />
            </div>

            {/* Find Near Me Button */}
            <div className="flex flex-col items-center gap-3">
              <Button
                onClick={handleFindNearMe}
                disabled={locating}
                variant="outline"
                className="flex items-center gap-2 px-6 py-2 border-teal text-teal hover:bg-teal hover:text-white transition-colors"
                data-testid="find-near-me-button"
              >
                {locating ? (
                  <>
                    <SpinnerGap className="w-5 h-5 animate-spin" />
                    <span>Finding your location...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="w-5 h-5" weight="fill" />
                    <span>Find providers near me</span>
                  </>
                )}
              </Button>
              
              {locationError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                  <Warning className="w-4 h-4" />
                  <span>{locationError}</span>
                </div>
              )}

              {/* Cross-tool links */}
              <div className="flex flex-wrap items-center justify-center gap-4 mt-2 text-sm text-slate-400">
                <Link to="/find-a-doctor" className="hover:text-teal transition-colors">
                  Don't know the medical term? <span className="text-teal font-medium">Find a Doctor</span>
                </Link>
                <span className="hidden sm:inline">&middot;</span>
                <Link to="/pecos-lookup" className="hover:text-teal transition-colors" data-testid="home-pecos-link">
                  Looking for PECOS Medicare enrollment status? <span className="text-teal font-medium">PECOS Lookup</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pro/Team features showcase — surfaces bulk/monitoring/reconcile tools */}
      <ProFeaturesShowcase />

      {/* Specialties Section */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 text-center mb-3">
            Browse by Specialty
          </h2>
          <p className="text-slate-500 text-center mb-10 max-w-xl mx-auto">
            Click a specialty to find healthcare providers in that field
          </p>

          <div 
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4"
            data-testid="specialty-grid"
          >
            {SPECIALTIES.map((specialty) => (
              <SpecialtyCard 
                key={specialty.slug} 
                specialty={specialty}
                onClick={handleSpecialtyClick}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Browse by State Section */}
      <section className="bg-slate-50 py-16" data-testid="browse-by-state">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 text-center mb-3">
            Browse by State
          </h2>
          <p className="text-slate-500 text-center mb-10 max-w-xl mx-auto">
            Find healthcare providers in your state
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
            {ALL_STATES.map((state) => (
              <Link
                key={state.code}
                to={`/state/${state.code}`}
                className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-slate-200 hover:border-teal hover:shadow-sm transition-all text-sm text-slate-700 hover:text-teal group"
                data-testid={`state-link-${state.code}`}
              >
                <MapPin className="w-3.5 h-3.5 text-slate-400 group-hover:text-teal shrink-0" weight="fill" />
                <span className="truncate">{state.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pro/Team features showcase — surfaces bulk/monitoring/reconcile tools */}

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                Instant Search
              </h3>
              <p className="text-slate-500 text-sm">
                Search by name, NPI number, or specialty. Get results in under a second.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                Official Data
              </h3>
              <p className="text-slate-500 text-sm">
                Data sourced directly from the CMS NPI Registry. Always accurate and up-to-date.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                Find Near You
              </h3>
              <p className="text-slate-500 text-sm">
                Use your location to find healthcare providers within 10 miles of you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Verified — last before footer */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RecentlyVerifiedWidget />
        </div>
      </section>
    </>
  );
};

export default HomePage;
